// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import { useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  CodeBlockIcon,
  Column,
  EmailIcon,
  EmptyState,
  MultiSelect,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Row,
  SearchInput,
  Text,
  TextWithTooltip,
} from "@hightouch/htui";
import { useDebounce } from "use-debounce";

import {
  type ContentEmailComponentsQuery,
  useContentEmailComponentTypesQuery,
} from "src/graphql";
import {
  ASSET_CARD_ASPECT_RATIO,
  AssetCardFooter,
  AssetCardMedia,
  AssetGrid,
  AssetGridSkeleton,
  AssetPickerModal,
  AssetSearchPane,
  LoadMoreFooter,
  MissingAssetPlaceholder,
  SelectableAssetCard,
  type AssetSource,
} from "src/components/asset-picker";
import { isEmailHtmlEmpty } from "src/cep/messages/utils";
import { contentEmailBrandProfileIds } from "./brand-profile-clause";
import { FillEmailPreview } from "./fill-email-preview";
import { useComponentSearch } from "./use-component-search";
import { useTemplateSearch } from "./use-template-search";
import type { EmailAssetKind, SelectedEmailAsset } from "./types";

const PREVIEW_WIDTH = 600;

type ComponentRow = ContentEmailComponentsQuery["content_emails"][number];

const toSelectedComponent = (component: ComponentRow): SelectedEmailAsset => ({
  id: component.id,
  kind: "component",
  name: component.name,
  componentType: component.component_type,
  html: component.content_email_version?.resolved_html?.html ?? null,
  brandProfileIds: contentEmailBrandProfileIds(component),
});

// Per-source copy for the rail, the derived title, and the confirm button.
const SOURCE_COPY: Record<
  EmailAssetKind,
  { label: string; noun: string; icon: React.ElementType }
> = {
  component: { label: "Components", noun: "component", icon: CodeBlockIcon },
  template: { label: "Templates", noun: "template", icon: EmailIcon },
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  initialSelected: SelectedEmailAsset[];
  onConfirm: (assets: SelectedEmailAsset[]) => void;
  /** Which sources appear in the left rail, in order. */
  sources: EmailAssetKind[];
  /** Per-kind selection caps — picking beyond a kind's cap is blocked. */
  maxSelected?: Partial<Record<EmailAssetKind, number>>;
  /**
   * Scope both sources to brand profiles: rows with zero live assignments
   * apply to all profiles, otherwise a live assignment for one of these
   * profiles is required. Omit for the unscoped workspace-wide pool.
   */
  brandProfileIds?: readonly string[] | null;
};

/**
 * Email asset picker built on the shared asset-picker shell, with configurable
 * sources: email components (search by name, filter by component type) and/or
 * standalone email templates (search by name). Each card previews the asset's
 * rendered thumbnail, hovering a card shows the full email, and selection is
 * local until confirmed.
 */
export const EmailAssetSelectionModal = ({
  isOpen,
  onClose,
  initialSelected,
  onConfirm,
  sources,
  maxSelected,
  brandProfileIds,
}: Props) => {
  const [selected, setSelected] =
    useState<SelectedEmailAsset[]>(initialSelected);

  const showComponents = sources.includes("component");
  const showTemplates = sources.includes("template");

  const { data: typeData } = useContentEmailComponentTypesQuery(undefined, {
    enabled: showComponents,
  });
  const componentTypeOptions = useMemo(
    () =>
      (typeData?.content_emails ?? [])
        .map((row) => row.component_type)
        .filter((type): type is string => Boolean(type)),
    [typeData],
  );

  // Unfiltered totals for the rail badges (both keep the brand-profile scope
  // — that's the pool each pane can ever show).
  const componentsTotal = useComponentSearch({
    search: "",
    componentTypes: [],
    brandProfileIds,
    enabled: showComponents,
  });
  const templatesTotal = useTemplateSearch({
    search: "",
    brandProfileIds,
    enabled: showTemplates,
  });

  // Caps apply per kind (e.g. templates capped at 10 while components stay
  // uncapped in the same modal).
  const atCap = (kind: EmailAssetKind) => {
    const cap = maxSelected?.[kind];
    return (
      cap !== undefined &&
      selected.filter((asset) => asset.kind === kind).length >= cap
    );
  };

  const toggle = (asset: SelectedEmailAsset) => {
    const isDeselecting = selected.some((a) => a.id === asset.id);
    // At the kind's cap, only deselection is allowed (the cards' checkboxes
    // are also disabled — this guards the card-body click path).
    if (!isDeselecting && atCap(asset.kind)) return;
    setSelected(
      isDeselecting
        ? (prev) => prev.filter((a) => a.id !== asset.id)
        : (prev) => [...prev, asset],
    );
  };

  const selectedIds = useMemo(
    () => new Set(selected.map((a) => a.id)),
    [selected],
  );

  // The templates pane rides the shared AssetSearchPane; its data hook maps
  // rows to the picker's SelectedEmailAsset shape once here so the card
  // renderer and the selection stay in sync. Defined inside the component to
  // close over the brand-profile scope.
  // TODO(eric.lovett): `useTemplatePaneData` is defined inside
  // `EmailAssetSelectionModal`, so every render passes the pane a new hook
  // function. Please consider a module-level hook that takes `brandProfileIds`
  // as an argument.
  // biome-ignore lint/nursery/noComponentHookFactories: see TODO above
  const useTemplatePaneData = (search: string) => {
    const { templates, ...rest } = useTemplateSearch({
      search,
      brandProfileIds,
    });
    const items = useMemo(
      () =>
        templates.map(
          (template): SelectedEmailAsset => ({
            id: template.id,
            kind: "template",
            name: template.name,
            componentType: null,
            html: template.content_email_version?.resolved_html?.html ?? null,
            brandProfileIds: contentEmailBrandProfileIds(template),
          }),
        ),
      [templates],
    );
    return { items, ...rest };
  };

  const componentsSource: AssetSource = {
    id: "components",
    label: SOURCE_COPY.component.label,
    icon: SOURCE_COPY.component.icon,
    count: componentsTotal.totalCount,
    isLoadingCount: componentsTotal.isLoading,
    pane: (
      <ComponentsPane
        typeOptions={componentTypeOptions}
        selectedIds={selectedIds}
        selectionDisabled={atCap("component")}
        brandProfileIds={brandProfileIds}
        onToggle={toggle}
      />
    ),
  };
  const templatesSource: AssetSource = {
    id: "templates",
    label: SOURCE_COPY.template.label,
    icon: SOURCE_COPY.template.icon,
    count: templatesTotal.totalCount,
    isLoadingCount: templatesTotal.isLoading,
    pane: (
      <AssetSearchPane
        label="template"
        pluralLabel="templates"
        searchPlaceholder="Search templates…"
        useData={useTemplatePaneData}
        emptyState={(hasSearch) => ({
          title: hasSearch ? "No matching templates" : "No templates yet",
          message: hasSearch
            ? "Try a different name."
            : "Create email templates to reference them here.",
        })}
        results={{
          layout: "grid",
          renderItem: (template) => (
            <EmailAssetCard
              key={template.id}
              asset={template}
              selected={selectedIds.has(template.id)}
              selectionDisabled={atCap("template")}
              onToggle={toggle}
            />
          ),
        }}
      />
    ),
  };

  // The rail lists the sources in the caller's order; the first is the
  // default view.
  const modalSources: AssetSource[] = sources.map((kind) =>
    kind === "component" ? componentsSource : templatesSource,
  );

  // "Select components", "Select templates", or e.g. "Select templates and
  // components" for multi-source callers.
  const title = `Select ${modalSources
    .map((source) => source.label.toLowerCase())
    .join(" and ")}`;
  const soleSource = sources.length === 1 ? sources[0] : undefined;
  const noun = soleSource ? SOURCE_COPY[soleSource].noun : "asset";
  const cappedKinds = sources.filter((kind) => atCap(kind));

  return (
    <AssetPickerModal
      isOpen={isOpen}
      title={title}
      onClose={onClose}
      sources={modalSources}
      selectedCount={selected.length}
      selectedPane={
        <SelectedPane selected={selected} noun={noun} onToggle={toggle} />
      }
      footer={
        <Row align="center" gap={4}>
          {cappedKinds.map((kind) => (
            <Text key={kind} size="sm" color="text.secondary">
              Select at most {maxSelected?.[kind]} {SOURCE_COPY[kind].noun}s
            </Text>
          ))}
          <ButtonGroup>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={() => onConfirm(selected)}>
              {selected.length > 0
                ? `Add ${selected.length} ${noun}${selected.length === 1 ? "" : "s"}`
                : "Done"}
            </Button>
          </ButtonGroup>
        </Row>
      }
    />
  );
};

const ComponentsPane = ({
  typeOptions,
  selectedIds,
  selectionDisabled,
  brandProfileIds,
  onToggle,
}: {
  typeOptions: string[];
  selectedIds: Set<string>;
  selectionDisabled: boolean;
  brandProfileIds?: readonly string[] | null;
  onToggle: (asset: SelectedEmailAsset) => void;
}) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);
  const [componentTypes, setComponentTypes] = useState<string[]>([]);

  const {
    components,
    totalCount,
    isLoading,
    isRefreshing,
    isLoadingMore,
    hasMore,
    loadMore,
  } = useComponentSearch({
    search: debouncedSearch,
    componentTypes,
    brandProfileIds,
  });

  const showSkeleton = isLoading || (isRefreshing && components.length === 0);
  const hasFilters = Boolean(debouncedSearch) || componentTypes.length > 0;

  return (
    <Column gap={4} height="100%" minHeight={0}>
      <Column gap={4} flexShrink={0}>
        <Row gap={2} align="center" width="100%">
          <Box flex={1} minWidth={0}>
            <SearchInput
              width="100%"
              placeholder="Search components…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          {typeOptions.length > 0 && (
            <MultiSelect
              placeholder="All types"
              options={typeOptions}
              optionLabel={(type) => type}
              optionValue={(type) => type}
              value={componentTypes}
              onChange={setComponentTypes}
            />
          )}
        </Row>

        <Row align="center" gap={2}>
          <Text fontWeight="medium">
            {hasFilters ? "Results" : "All components"}
          </Text>
          {!showSkeleton && (
            <Text size="sm" color="text.secondary" mt={0.5}>
              {totalCount} component{totalCount === 1 ? "" : "s"}
            </Text>
          )}
        </Row>
      </Column>

      <Column flex={1} minHeight={0} overflowY="auto" gap={4}>
        {showSkeleton ? (
          <AssetGridSkeleton />
        ) : components.length === 0 ? (
          <EmptyState
            title={hasFilters ? "No matching components" : "No components yet"}
            message={
              hasFilters
                ? "Try a different name or type."
                : "Import components to your library to reference them here."
            }
          />
        ) : (
          <>
            <AssetGrid>
              {components.map((component) => (
                <EmailAssetCard
                  key={component.id}
                  asset={toSelectedComponent(component)}
                  selected={selectedIds.has(component.id)}
                  selectionDisabled={selectionDisabled}
                  onToggle={onToggle}
                />
              ))}
            </AssetGrid>
            <LoadMoreFooter
              shownCount={components.length}
              totalCount={totalCount}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
              onLoadMore={loadMore}
              label="components"
            />
          </>
        )}
      </Column>
    </Column>
  );
};

// The Selected view keeps the already-picked assets even if they scroll out of
// the current search page, so it renders from the stored SelectedEmailAsset
// (which carries its own html) rather than a fresh query row.
const SelectedPane = ({
  selected,
  noun,
  onToggle,
}: {
  selected: SelectedEmailAsset[];
  noun: string;
  onToggle: (asset: SelectedEmailAsset) => void;
}) => {
  if (selected.length === 0) {
    return (
      <EmptyState
        title="Nothing selected"
        message={`Pick ${noun}s from the library — everything you select shows up here.`}
      />
    );
  }

  return (
    <Column height="100%" minHeight={0} overflowY="auto">
      <AssetGrid>
        {selected.map((asset) => (
          <EmailAssetCard
            key={asset.id}
            asset={asset}
            selected
            selectionDisabled={false}
            onToggle={onToggle}
          />
        ))}
      </AssetGrid>
    </Column>
  );
};

const EmailAssetCard = ({
  asset,
  selected,
  selectionDisabled,
  onToggle,
}: {
  asset: SelectedEmailAsset;
  selected: boolean;
  /** At the selection cap, unselected cards can't be picked. */
  selectionDisabled: boolean;
  onToggle: (asset: SelectedEmailAsset) => void;
}) => {
  // A component still generating has no resolved HTML yet, so show a "still
  // processing" placeholder instead of a preview. Selection stays allowed:
  // ID-only consumers (agent chat, campaign intake) can reference it right away,
  // and the one consumer that needs the markup — the email editor insert flow —
  // filters these out at insert time and tells the user they were skipped.
  const isProcessing = !asset.html;
  const hasPreview = useMemo(() => !isEmailHtmlEmpty(asset.html), [asset.html]);

  const card = (
    <Box sx={{ aspectRatio: ASSET_CARD_ASPECT_RATIO }}>
      <SelectableAssetCard
        selected={selected}
        checkboxDisabled={!selected && selectionDisabled}
        onToggleSelect={() => onToggle(asset)}
        onClick={() => onToggle(asset)}
        footer={
          <AssetCardFooter
            title={asset.name}
            subtitle={asset.componentType || undefined}
          />
        }
      >
        <AssetCardMedia>
          {isProcessing ? (
            <MissingAssetPlaceholder label="Still processing…" />
          ) : (
            <FillEmailPreview
              html={asset.html}
              wrapAsComponent={asset.kind === "component"}
            />
          )}
        </AssetCardMedia>
      </SelectableAssetCard>
    </Box>
  );

  if (!hasPreview) return card;

  return (
    <Popover
      trigger="hover"
      placement="auto"
      strategy="fixed"
      isLazy
      gutter={12}
    >
      <PopoverTrigger>{card}</PopoverTrigger>
      <Portal>
        <PopoverContent
          p={0}
          overflow="hidden"
          w={`${PREVIEW_WIDTH}px`}
          maxW={`${PREVIEW_WIDTH}px`}
          zIndex="tooltip"
        >
          <Box px={3} py={2} borderBottom="1px solid" borderColor="base.border">
            <TextWithTooltip size="sm" fontWeight="medium">
              {asset.name}
            </TextWithTooltip>
          </Box>
          <Box h="70vh" bg="white">
            <FillEmailPreview
              html={asset.html}
              scrollable
              wrapAsComponent={asset.kind === "component"}
              resetScrollOnMouseLeave={false}
            />
          </Box>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
